import { createSlice } from '@reduxjs/toolkit';
import {
  adminToggleBlockUser,
  adminToggleDeleteComment,
  adminToggleDeleteDiscussion,
  getAdminCommentList,
  getAdminDiscussionList,
} from '../actions/adminCommentActions';
import { getPageFromUrl } from '../../../utils/paginationUtil';

const initialState: AdminCommentModerationStateType = {
  comments: [],
  discussions: [],
  isLoading: false,
  discussionsLoading: false,
  errorMessage: '',
  paginator: { count: 0, next: null, previous: null, currentPage: 1, pageSize: 12 },
  discussionsPaginator: { count: 0, next: null, previous: null, currentPage: 1, pageSize: 12 },
};

const adminCommentSlice = createSlice({
  name: 'adminComments',
  initialState,
  reducers: {},
  extraReducers: builder => {
    // ── Comments list ──────────────────────────────────────────────────────────
    builder.addCase(getAdminCommentList.pending, state => {
      state.isLoading = true;
      state.errorMessage = '';
    });
    builder.addCase(getAdminCommentList.fulfilled, (state, action) => {
      state.isLoading = false;
      state.comments = action.payload.results;
      state.paginator = {
        count: action.payload.count,
        next: getPageFromUrl(action.payload.next),
        previous: getPageFromUrl(action.payload.previous),
        currentPage: action.meta.arg.page ?? 1,
        pageSize: action.payload.page_size,
      };
    });
    builder.addCase(getAdminCommentList.rejected, (state, action) => {
      state.isLoading = false;
      state.errorMessage = action.payload?.detail || 'Failed to load comments.';
    });

    // ── Discussions list ───────────────────────────────────────────────────────
    builder.addCase(getAdminDiscussionList.pending, state => {
      state.discussionsLoading = true;
      state.errorMessage = '';
    });
    builder.addCase(getAdminDiscussionList.fulfilled, (state, action) => {
      state.discussionsLoading = false;
      state.discussions = action.payload.results;
      state.discussionsPaginator = {
        count: action.payload.count,
        next: getPageFromUrl(action.payload.next),
        previous: getPageFromUrl(action.payload.previous),
        currentPage: action.meta.arg.page ?? 1,
        pageSize: action.payload.page_size,
      };
    });
    builder.addCase(getAdminDiscussionList.rejected, (state, action) => {
      state.discussionsLoading = false;
      state.errorMessage = action.payload?.detail || 'Failed to load discussions.';
    });

    // ── Toggle delete comment ──────────────────────────────────────────────────
    builder.addCase(adminToggleDeleteComment.fulfilled, (state, action) => {
      const updated = action.payload.comment;
      const idx = state.comments.findIndex(c => c.id === updated.id);
      if (idx !== -1) state.comments[idx] = updated;
    });
    builder.addCase(adminToggleDeleteComment.rejected, (state, action) => {
      state.errorMessage = action.payload?.detail || 'Failed to update comment.';
    });

    // ── Toggle delete discussion ───────────────────────────────────────────────
    builder.addCase(adminToggleDeleteDiscussion.fulfilled, (state, action) => {
      const updated = action.payload.discussion;
      const idx = state.discussions.findIndex(d => d.id === updated.id);
      if (idx !== -1) state.discussions[idx] = updated;
    });
    builder.addCase(adminToggleDeleteDiscussion.rejected, (state, action) => {
      state.errorMessage = action.payload?.detail || 'Failed to update discussion.';
    });

    // ── Toggle block user ──────────────────────────────────────────────────────
    builder.addCase(adminToggleBlockUser.fulfilled, (state, action) => {
      const { user_id, is_comment_blocked } = action.payload;
      state.comments.forEach(c => {
        if (c.user_id === user_id) c.is_user_comment_blocked = is_comment_blocked;
      });
    });
    builder.addCase(adminToggleBlockUser.rejected, (state, action) => {
      state.errorMessage = action.payload?.detail || 'Failed to block/unblock user.';
    });
  },
});

export default adminCommentSlice.reducer;
